import { useState, useEffect, useCallback } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import DataFormTitle from '@App/components/DataFormTitle/DataFormTitle'
import VisibilityToggles from '@App/components/VisibilityToggles/VisibilityToggles'
import TypeSelect from '@App/components/TypeSelect/TypeSelect'
import { createMissionData } from '@services/data.service'

import './alert-form.styl'

function AlertForm() {
  const navigate = useNavigate()
  const { map, refetchAlerts, alertDataTypes } = useOutletContext()
  const { activeProject } = useSelector(state => state.missionSetup)

  const [name, setName] = useState('')
  const [visibility, setVisibility] = useState('private')
  const [description, setDescription] = useState('')
  const [pointGeometry, setPointGeometry] = useState(null)
  const [loading, setLoading] = useState(false)
  const [activeAlertLevel, setActiveAlertLevel] = useState()

  const onSubmit = useCallback((e) => {
    e.preventDefault()
    setLoading(true)
    createMissionData({
      type: 'Feature',
      id: name,
      geometry: pointGeometry,
      properties: {
        id: name,
        mission: activeProject.id,
        dataType: activeAlertLevel,
        description,
        source: 'custom',
        private: visibility === 'private',
      },
    }).then(() => {
      navigate('/alerts')
      refetchAlerts()
    }).finally(() => setLoading(false))
  }, [name, description, activeProject, activeAlertLevel, pointGeometry, visibility, navigate, refetchAlerts])

  useEffect(() => {
    if (!map) return

    const canvasContainer = document.querySelector('.maplibregl-canvas')
    if (canvasContainer) canvasContainer.classList.add('crosshair-cursor')

    if (map.getLayer('alerts-layer')) {
      map.setPaintProperty('alerts-layer', 'icon-opacity', 0.4)
      map.setLayoutProperty('alerts-layer', 'icon-allow-overlap', false)
    }
    if (map.getLayer('alerts-layer-bg')) {
      map.setPaintProperty('alerts-layer-bg', 'icon-opacity', 0.4)
      map.setLayoutProperty('alerts-layer-bg', 'icon-allow-overlap', false)
    }

    const handleClick = (e) => {
      setPointGeometry({
        type: 'Point',
        coordinates: [e.lngLat.lng, e.lngLat.lat],
      })
    }

    map.on('click', handleClick)

    return () => {
      if (canvasContainer) canvasContainer.classList.remove('crosshair-cursor')

      if (map) {
        map.off('click', handleClick)
        if (map.getLayer && map.getLayer('alerts-layer')) {
          map.setPaintProperty('alerts-layer', 'icon-opacity', 1)
          map.setLayoutProperty('alerts-layer', 'icon-allow-overlap', true)
        }
        if (map.getLayer && map.getLayer('alerts-layer-bg')) {
          map.setPaintProperty('alerts-layer-bg', 'icon-opacity', 1)
          map.setLayoutProperty('alerts-layer-bg', 'icon-allow-overlap', true)
        }
      }
    }
  }, [map])

  useEffect(() => {
    if (!map) return

    const source = map.getSource('new-alert')

    const pointFeature = {
      type: 'Feature',
      properties: {
        dataType: activeAlertLevel,
        color: alertDataTypes?.find(type => type.id === activeAlertLevel)?.color || '#000000',
      },
      geometry: pointGeometry,
    }

    source.setData({ type: 'FeatureCollection', features: [pointFeature] })

    return () => {
      source.setData({ type: 'FeatureCollection', features: [] })
    }
  }, [map, activeAlertLevel, pointGeometry, alertDataTypes])

  return (
    <form
      className={`alert-form ${loading ? 'alert-form--loading' : ''}`}
      onSubmit={onSubmit}
    >
      <DataFormTitle>Name</DataFormTitle>
      <div className="alert-form__input-field">
        <input type="text" value={name} onChange={e => setName(e.target.value)} />
      </div>
      <DataFormTitle>Description</DataFormTitle>
      <div className="alert-form__input-field">
        <textarea value={description} onChange={e => setDescription(e.target.value)} />
      </div>
      <DataFormTitle>Alert level</DataFormTitle>
      <div className="alert-form__input-field">
        <TypeSelect
          value={activeAlertLevel}
          onChange={setActiveAlertLevel}
          types={alertDataTypes}
          useColor={true}
        />
      </div>
      <DataFormTitle>Data location</DataFormTitle>
      <div className="alert-form__input-field">
        {pointGeometry
          ? (
              <p>
                Coordindates:
                {' '}
                {pointGeometry.coordinates[1].toFixed(4)}
                {' lat, '}
                {pointGeometry.coordinates[0].toFixed(4)}
                {' lng'}
              </p>
            )
          : (
              <p className="alert-form__input-instruction">
                Click on the map to set the alert location.
              </p>
            )}
      </div>
      <DataFormTitle>Visibility</DataFormTitle>
      <VisibilityToggles
        value={visibility}
        onChange={setVisibility}
      />
      <button
        className="alert-form__submit"
        type="submit"
        disabled={!name || !pointGeometry}
      >
        Submit
      </button>
    </form>
  )
}

export default AlertForm
